/* Blackwater Soap Guild — <candells-accordion>, the shared accordion behaviour for
   the Details section and the Product FAQ.

   Markup contract (snippets/candells-accordion-row.liquid):
     <candells-accordion [data-single-open]>
       .candells-accordion__row[id][data-candells-accordion-row][data-open?]
         h3/h4.candells-accordion__heading
           button.candells-accordion__trigger[aria-expanded][aria-controls]
         .candells-accordion__panel[role=region][aria-labelledby]
           .candells-accordion__body

   Why a custom element and not an init() sweep: the theme editor tears a section
   down and re-renders it on every settings change, and a custom element re-binds
   itself on connect for free — no shopify:section:load bookkeeping, and no risk
   of double-binding. Two sections on the product page load this same file, so the
   define() is guarded.

   Why not <details>/<summary>: the spec asks for a height transition on the panel,
   which <details> cannot animate without JS anyway, plus single-open across rows.
   The no-JS story is covered instead by the CSS `:not(:defined)` rule, which
   leaves every panel open until (or unless) this file runs.

   max-height is animated from a measured pixel height and then released to `none`
   once the transition ends: the INCI list is longer than any fixed cap we could
   pick (the prototype's 420px would clip it), and a row can also grow after it is
   open — a rich text metafield, a loaded image, a font swap. Closing reverses the
   trick: `none` cannot be transitioned from, so the current height is written back
   as pixels and forced to lay out before the collapse to 0. */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('candells-accordion')) return;

  var ROW_SELECTOR = '.candells-accordion__row';
  var TRIGGER_SELECTOR = '.candells-accordion__trigger';
  var PANEL_SELECTOR = '.candells-accordion__panel';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  class CandellsAccordionElement extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onHashChange = this.onHashChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      window.addEventListener('hashchange', this.onHashChange);

      var rows = this.rows();
      for (var i = 0; i < rows.length; i++) {
        this.prepare(rows[i]);
      }

      /* A row the caller server-rendered open still has max-height 0 from the CSS;
         release it without animating, so the first paint is already correct. */
      for (var j = 0; j < rows.length; j++) {
        if (rows[j].hasAttribute('data-open')) {
          var panel = rows[j].querySelector(PANEL_SELECTOR);
          if (panel) panel.style.maxHeight = 'none';
        }
      }

      this.openFromHash(window.location.hash, false);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('hashchange', this.onHashChange);
    }

    rows() {
      return this.querySelectorAll(ROW_SELECTOR);
    }

    /* One listener per panel, attached once. Bound here rather than per open()
       call so a row that is closed mid-transition cannot swallow the handler that
       was meant to release its max-height. */
    prepare(row) {
      var panel = row.querySelector(PANEL_SELECTOR);
      if (!panel || panel.dataset.candellsAccordionBound) return;
      panel.dataset.candellsAccordionBound = 'true';
      panel.addEventListener('transitionend', function (event) {
        if (event.target !== panel || event.propertyName !== 'max-height') return;
        if (row.hasAttribute('data-open')) panel.style.maxHeight = 'none';
      });
    }

    onClick(event) {
      var trigger = event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
      if (trigger && this.contains(trigger)) {
        var row = trigger.closest(ROW_SELECTOR);
        if (row) this.toggle(row);
        return;
      }

      /* In-page links inside a panel (the FAQ answers point at #ingredients and
         #how-to-use). hashchange covers the case where the hash actually changes;
         this covers re-clicking a link to the hash the page is already on, which
         fires no event at all.

         A cross-section link (an FAQ answer pointing at a Details row) targets a row
         this element does not own, so it has to be offered to the other accordions on
         the page too: relying on their hashchange listener alone silently dead-clicks
         once the page is already sitting on that hash. Re-opening an already-open row
         is a no-op, so the duplicate pass on a real hash change costs nothing. */
      var link = event.target.closest ? event.target.closest('a[href^="#"]') : null;
      if (!link || !this.contains(link)) return;
      var hash = link.getAttribute('href');
      if (!hash || hash.length < 2) return;
      if (this.openFromHash(hash, true)) return;

      var others = document.querySelectorAll('candells-accordion');
      for (var k = 0; k < others.length; k++) {
        if (others[k] === this || typeof others[k].openFromHash !== 'function') continue;
        if (others[k].openFromHash(hash, true)) return;
      }
    }

    onHashChange() {
      this.openFromHash(window.location.hash, true);
    }

    /* Matched on row.id rather than querySelector so an id the owner typed (with a
       space, a colon, a leading digit) cannot throw an invalid-selector error.
       Returns whether this element owns the target row, so a caller can hand an
       unmatched hash on to the other accordions on the page. */
    openFromHash(hash, scroll) {
      if (!hash || hash.charAt(0) !== '#' || hash.length < 2) return false;
      var id = hash.slice(1);
      var rows = this.rows();
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].id !== id) continue;
        if (!rows[i].hasAttribute('data-open')) this.open(rows[i]);
        if (scroll && rows[i].scrollIntoView) {
          rows[i].scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'start'
          });
        }
        return true;
      }
      return false;
    }

    toggle(row) {
      if (row.hasAttribute('data-open')) this.close(row);
      else this.open(row);
    }

    open(row) {
      if (this.hasAttribute('data-single-open')) {
        var rows = this.rows();
        for (var i = 0; i < rows.length; i++) {
          if (rows[i] !== row && rows[i].hasAttribute('data-open')) this.close(rows[i]);
        }
      }

      var trigger = row.querySelector(TRIGGER_SELECTOR);
      var panel = row.querySelector(PANEL_SELECTOR);
      row.setAttribute('data-open', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (!panel) return;

      if (prefersReducedMotion()) {
        panel.style.maxHeight = 'none';
        return;
      }
      panel.style.maxHeight = panel.scrollHeight + 'px';
    }

    close(row) {
      var trigger = row.querySelector(TRIGGER_SELECTOR);
      var panel = row.querySelector(PANEL_SELECTOR);

      if (panel && !prefersReducedMotion()) {
        panel.style.maxHeight = panel.scrollHeight + 'px';
        /* Read a layout property so the pixel height is committed before the next
           write; without it the browser coalesces both and there is no transition. */
        void panel.offsetHeight;
      }

      row.removeAttribute('data-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (!panel) return;
      panel.style.maxHeight = prefersReducedMotion() ? '' : '0px';
    }
  }

  customElements.define('candells-accordion', CandellsAccordionElement);
})();
